import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import {
  Perm,
  type Product,
  type ProductListItem,
  type Transfer,
  type TransferDetail,
  type Warehouse,
} from '../api/types'
import { formatMoment, formatQty, toNumber } from '../ui/format'
import {
  Banner, Button, Empty, ErrorBanner, Field, NumberInput, Panel, Row, Select, Spinner, TextInput,
} from '@shop/ui'
import { transferStages, transferStatusNames } from '../ui/purchasing'

/**
 * التحويلُ بين المستودعات.
 *
 * <b>والمراحلُ الثلاثُ ظاهرةٌ دائمًا</b>: مسوّدة ⇐ في الطريق ⇐ وصل. والموظّفُ
 * يحتاج أن يرى أين وصل المستند، وحالةٌ واحدةٌ مكتوبةٌ لا تقول أيّها.
 * <b>وإن اختصرت الواجهةُ الخطوتين داخلَ الفرع، لا يُختصر الدفتر.</b>
 */
export default function Transfers() {
  const { can } = useAuth()
  const [status, setStatus] = useState('')

  const transfers = useQuery({
    queryKey: ['transfers', status],
    queryFn: () => api<Transfer[]>(`/api/v1/transfers?status=${status}`),
  })

  return (
    <Panel
      title="التحويلات"
      actions={can(Perm.transferCreate) && <NewTransferButton />}
    >
      <div className="mb-4">
        <Field label="الحالة">
          <Select value={status} onChange={setStatus}>
            <option value="">الكلّ</option>
            <option value="DRAFT">مسوّدة</option>
            <option value="SHIPPED">في الطريق</option>
            <option value="RECEIVED">وصل</option>
            <option value="CANCELLED">ملغًى</option>
            <option value="REVERSED">معكوس</option>
          </Select>
        </Field>
      </div>

      {transfers.isPending && <Spinner />}
      {transfers.isError && <ErrorBanner error={transfers.error} />}
      {transfers.data?.length === 0 && <Empty>لا تحويلاتٍ مطابقة.</Empty>}

      <ul className="space-y-2">
        {(transfers.data ?? []).map((transfer) => (
          <li key={transfer.id}>
            <Link
              to={`/transfers/${transfer.id}`}
              className="block rounded-md border border-line px-3 py-2 hover:bg-surface"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">
                  {transfer.fromWarehouseName} ← {transfer.toWarehouseName}
                </span>
                <span className="tabular text-xs text-ink-soft" dir="ltr">{transfer.transferNo}</span>
              </div>

              <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                <span className={transfer.status === 'SHIPPED' ? 'text-warn' : ''}>
                  {transferStatusNames[transfer.status] ?? transfer.status}
                </span>

                <span className="tabular">{formatQty(transfer.shippedQty)} قطعة</span>

                {transfer.inTransitQty > 0 && (
                  <span className="tabular text-warn">
                    في الطريق {formatQty(transfer.inTransitQty)}
                  </span>
                )}

                <span className="tabular">{transfer.transferDate}</span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  )
}

function NewTransferButton() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const warehouses = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => api<Warehouse[]>('/api/v1/warehouses'),
    enabled: open,
  })

  const create = useMutation({
    mutationFn: () =>
      api<Transfer>('/api/v1/transfers', {
        method: 'POST',
        body: { fromWarehouseId: from, toWarehouseId: to },
      }),
    onSuccess: async (transfer) => {
      await queryClient.invalidateQueries({ queryKey: ['transfers'] })
      setOpen(false)
      void navigate(`/transfers/${transfer.id}`)
    },
  })

  if (!open) {
    return <Button tone="primary" onClick={() => setOpen(true)}>تحويلٌ جديد</Button>
  }

  return (
    <div className="w-full space-y-3 rounded-md border border-line bg-surface p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="من مستودع">
          <Select value={from} onChange={setFrom}>
            <option value="">اختر المصدر…</option>
            {(warehouses.data ?? []).map((w) => (
              <option key={w.id} value={w.id}>{w.nameAr}</option>
            ))}
          </Select>
        </Field>

        <Field label="إلى مستودع">
          <Select value={to} onChange={setTo}>
            <option value="">اختر الوجهة…</option>
            {(warehouses.data ?? []).filter((w) => w.id !== from).map((w) => (
              <option key={w.id} value={w.id}>{w.nameAr}</option>
            ))}
          </Select>
        </Field>
      </div>

      {create.isError && <ErrorBanner error={create.error} />}

      <div className="flex gap-2">
        <Button
          tone="primary"
          disabled={!from || !to || from === to || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'يُنشأ…' : 'إنشاء'}
        </Button>

        <Button onClick={() => setOpen(false)}>إلغاء</Button>
      </div>
    </div>
  )
}

export function TransferDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const { can } = useAuth()

  const transfer = useQuery({
    queryKey: ['transfer', id],
    queryFn: () => api<TransferDetail>(`/api/v1/transfers/${id}`),
    enabled: Boolean(id),
  })

  if (transfer.isPending) {
    return <Spinner />
  }

  if (transfer.isError) {
    return <ErrorBanner error={transfer.error} />
  }

  const { header, lines } = transfer.data!

  return (
    <div className="space-y-4">
      <Panel title={`تحويل ${header.transferNo}`}>
        <Stages status={header.status} />

        <dl className="mt-4 space-y-1 text-sm">
          <Row label="من">{header.fromWarehouseName}</Row>
          <Row label="إلى">{header.toWarehouseName}</Row>
          <Row label="الحالة">{transferStatusNames[header.status] ?? header.status}</Row>
          <Row label="التاريخ">{header.transferDate}</Row>

          {header.shippedAt && <Row label="شُحن">{formatMoment(header.shippedAt)}</Row>}
          {header.receivedAt && <Row label="وصل">{formatMoment(header.receivedAt)}</Row>}

          {header.inTransitQty > 0 && (
            <Row label="في الطريق">
              <span className="tabular text-warn">{formatQty(header.inTransitQty)}</span>
            </Row>
          )}

          {header.cancelReason && <Row label="سببُ الإلغاء">{header.cancelReason}</Row>}
          {header.reverseReason && <Row label="سببُ العكس">{header.reverseReason}</Row>}
        </dl>

        {/* ⚠ داخلَ الفرع الواحد تُختصر الخطوتان في الواجهة — **والدفترُ يبقى
            أربعَ حركات**، فلا تقريرَ يعرف الفرق */}
        {header.isSameBranch && header.status === 'DRAFT' && (
          <div className="mt-3">
            <Banner tone="info">
              المستودعان في الفرع نفسِه، فتُعرض الخطوتان زرًّا واحدًا. <b>والدفترُ
              يبقى صريحًا</b>: المصدر ⇐ العبور ⇐ الوجهة.
            </Banner>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <Actions header={header} lineCount={lines.length} can={can} />
        </div>
      </Panel>

      <Panel title="السطور">
        {lines.length === 0 && <Empty>لا سطورَ بعد.</Empty>}

        <ul className="space-y-2">
          {lines.map((line) => (
            <li key={line.id} className="rounded-md border border-line px-3 py-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{line.productName}</span>
                <span className="tabular text-xs text-ink-soft" dir="ltr">{line.sku}</span>
              </div>

              <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                <span className="tabular">أُرسل {formatQty(line.qty)}</span>

                {header.status !== 'DRAFT' && (
                  <span className="tabular">وصل {formatQty(line.receivedQty)}</span>
                )}

                {line.inTransitQty > 0 && (
                  <span className="tabular text-warn">في الطريق {formatQty(line.inTransitQty)}</span>
                )}
              </div>
            </li>
          ))}
        </ul>

        {header.status === 'DRAFT' && can(Perm.transferCreate) && (
          <div className="mt-3">
            <TransferLineEditor transfer={header} lines={lines} />
          </div>
        )}
      </Panel>
    </div>
  )
}

/** شريطُ المراحل — <b>ثلاثٌ ظاهرةٌ دائمًا</b> لا حالةٌ واحدةٌ مكتوبة. */
function Stages({ status }: { status: string }) {
  const reached = transferStages.findIndex((stage) => stage.code === status)
  const finished = status === 'CANCELLED' || status === 'REVERSED'

  return (
    <ol className="flex flex-wrap items-center gap-2 text-xs">
      {transferStages.map((stage, index) => {
        const active = !finished && index <= (reached === -1 ? 0 : reached)

        return (
          <li key={stage.code} className="flex items-center gap-2">
            <span
              className={`rounded-full px-3 py-1 ${
                active ? 'bg-brand text-white' : 'border border-line text-ink-soft'
              }`}
            >
              {stage.label}
            </span>

            {index < transferStages.length - 1 && <span className="text-ink-soft">←</span>}
          </li>
        )
      })}

      {finished && (
        <li className="rounded-full border border-danger/50 px-3 py-1 text-danger">
          {transferStatusNames[status]}
        </li>
      )}
    </ol>
  )
}

function Actions({
  header,
  lineCount,
  can,
}: {
  header: Transfer
  lineCount: number
  can: (code: string) => boolean
}) {
  if (header.status === 'DRAFT') {
    return (
      <>
        {lineCount > 0 && can(Perm.transferShip) && <ShipControl transferId={header.id} />}
        {can(Perm.transferCreate) && <CancelControl transferId={header.id} />}
      </>
    )
  }

  if (header.status === 'SHIPPED') {
    return (
      <>
        {can(Perm.transferReceive) && <ReceiveControl transfer={header} />}
        {can(Perm.movementReverse) && <ReverseControl transferId={header.id} />}
      </>
    )
  }

  return null
}

function ShipControl({ transferId }: { transferId: string }) {
  const queryClient = useQueryClient()

  const ship = useMutation({
    mutationFn: () => api<TransferDetail>(`/api/v1/transfers/${transferId}/ship`, { method: 'POST', body: {} }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['transfer', transferId] })
      await queryClient.invalidateQueries({ queryKey: ['transfers'] })
      await queryClient.invalidateQueries({ queryKey: ['stock'] })
    },
  })

  return (
    <div className="w-full space-y-2">
      <Banner tone="warn">
        الشحنُ يُخرج البضاعةَ من المصدر إلى العبور. <b>وبعده لا يُلغى التحويل</b> —
        يُعكَس بسببٍ مكتوب.
      </Banner>

      {ship.isError && <ErrorBanner error={ship.error} />}

      <Button tone="primary" disabled={ship.isPending} onClick={() => ship.mutate()}>
        {ship.isPending ? 'يُشحن…' : 'شحنُ التحويل'}
      </Button>
    </div>
  )
}

/**
 * الاستلام — <b>والفرعُ المستقبِلُ يُقرّ بما وصله</b>.
 *
 * وما لم يصل يبقى في العبور ولا يُفترض وصولُه: يُشطب بتسويةٍ على مستودع
 * العبور بسبب «نقصٌ في الطريق».
 */
function ReceiveControl({ transfer }: { transfer: Transfer }) {
  const queryClient = useQueryClient()
  const [partial, setPartial] = useState(false)
  const [quantities, setQuantities] = useState<Record<string, string>>({})

  const detail = useQuery({
    queryKey: ['transfer', transfer.id],
    queryFn: () => api<TransferDetail>(`/api/v1/transfers/${transfer.id}`),
  })

  const receive = useMutation({
    mutationFn: () =>
      api<TransferDetail>(`/api/v1/transfers/${transfer.id}/receive`, {
        method: 'POST',
        body: partial
          ? {
              lines: (detail.data?.lines ?? []).map((line) => ({
                transferLineId: line.id,
                qty: toNumber(quantities[line.id] ?? line.qty.toString()) ?? line.qty,
              })),
            }
          : {},
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['transfer', transfer.id] })
      await queryClient.invalidateQueries({ queryKey: ['transfers'] })
      await queryClient.invalidateQueries({ queryKey: ['stock'] })
    },
  })

  return (
    <div className="w-full space-y-2">
      <label className="flex items-center gap-2 text-xs text-ink-soft">
        <input type="checkbox" checked={partial} onChange={(e) => setPartial(e.target.checked)} />
        وصل أقلُّ ممّا أُرسل
      </label>

      {partial && (
        <>
          <Banner tone="warn">
            ما لا يصل <b>يبقى في العبور</b> ولا يُفترض وصولُه — يُشطب بتسويةٍ على
            مستودع العبور بسبب «نقصٌ في الطريق».
          </Banner>

          {(detail.data?.lines ?? []).map((line) => (
            <Field key={line.id} label={line.productName} hint={`أُرسل ${formatQty(line.qty)}`}>
              <NumberInput
                value={quantities[line.id] ?? line.qty.toString()}
                onChange={(v) => setQuantities({ ...quantities, [line.id]: v })}
              />
            </Field>
          ))}
        </>
      )}

      {receive.isError && <ErrorBanner error={receive.error} />}

      <Button tone="primary" disabled={receive.isPending} onClick={() => receive.mutate()}>
        {receive.isPending ? 'يُستلَم…' : 'استلامُ التحويل'}
      </Button>
    </div>
  )
}

function CancelControl({ transferId }: { transferId: string }) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [asking, setAsking] = useState(false)

  const cancel = useMutation({
    mutationFn: () =>
      api<TransferDetail>(`/api/v1/transfers/${transferId}/cancel`, { method: 'POST', body: { reason } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['transfer', transferId] })
      await queryClient.invalidateQueries({ queryKey: ['transfers'] })
      setAsking(false)
    },
  })

  if (!asking) {
    return <Button onClick={() => setAsking(true)}>إلغاءُ التحويل</Button>
  }

  return (
    <div className="w-full space-y-2">
      <Field label="سببُ الإلغاء" hint="عشرةُ أحرفٍ فأكثر">
        <TextInput value={reason} onChange={setReason} placeholder="لم تعد الوجهةُ تحتاجها" />
      </Field>

      {cancel.isError && <ErrorBanner error={cancel.error} />}

      <div className="flex gap-2">
        <Button
          tone="danger"
          disabled={reason.trim().length < 10 || cancel.isPending}
          onClick={() => cancel.mutate()}
        >
          {cancel.isPending ? 'يُلغى…' : 'تأكيدُ الإلغاء'}
        </Button>

        <Button onClick={() => setAsking(false)}>تراجع</Button>
      </div>
    </div>
  )
}

function ReverseControl({ transferId }: { transferId: string }) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [asking, setAsking] = useState(false)

  const reverse = useMutation({
    mutationFn: () =>
      api<TransferDetail>(`/api/v1/transfers/${transferId}/reverse`, { method: 'POST', body: { reason } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['transfer', transferId] })
      await queryClient.invalidateQueries({ queryKey: ['transfers'] })
      await queryClient.invalidateQueries({ queryKey: ['stock'] })
      setAsking(false)
    },
  })

  if (!asking) {
    return <Button onClick={() => setAsking(true)}>عكسُ الشحنة</Button>
  }

  return (
    <div className="w-full space-y-2">
      <Banner tone="warn">
        البضاعةُ تعود من العبور إلى المصدر <b>بحركاتٍ جديدة</b> — ولا يُمحى أنّ
        التحويلَ شُحن يومًا.
      </Banner>

      <Field label="سببُ العكس" hint="عشرةُ أحرفٍ فأكثر">
        <TextInput value={reason} onChange={setReason} placeholder="الشاحنةُ لم تتحرّك أصلًا" />
      </Field>

      {reverse.isError && <ErrorBanner error={reverse.error} />}

      <div className="flex gap-2">
        <Button
          tone="danger"
          disabled={reason.trim().length < 10 || reverse.isPending}
          onClick={() => reverse.mutate()}
        >
          {reverse.isPending ? 'يُعكَس…' : 'تأكيدُ العكس'}
        </Button>

        <Button onClick={() => setAsking(false)}>تراجع</Button>
      </div>
    </div>
  )
}

type DraftLine = { variantId: string; label: string; qty: string }

function TransferLineEditor({
  transfer,
  lines,
}: {
  transfer: Transfer
  lines: TransferDetail['lines']
}) {
  const queryClient = useQueryClient()

  const [draft, setDraft] = useState<DraftLine[]>(() =>
    lines.map((l) => ({
      variantId: l.variantId,
      label: `${l.productName} · ${l.sku}`,
      qty: l.qty.toString(),
    })),
  )

  const save = useMutation({
    mutationFn: () =>
      api<TransferDetail>(`/api/v1/transfers/${transfer.id}/lines`, {
        method: 'PUT',
        body: { lines: draft.map((d) => ({ variantId: d.variantId, qty: toNumber(d.qty) ?? 0 })) },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['transfer', transfer.id] })
    },
  })

  const incomplete = draft.some((d) => (toNumber(d.qty) ?? 0) <= 0)

  return (
    <div className="space-y-3">
      {draft.map((line, index) => (
        <div key={`${line.variantId}-${index}`} className="rounded-md border border-line p-3">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <span className="text-sm font-medium">{line.label}</span>
            <Button onClick={() => setDraft(draft.filter((_, i) => i !== index))}>حذف</Button>
          </div>

          <Field label="الكميّة">
            <NumberInput
              value={line.qty}
              onChange={(v) => setDraft(draft.map((d, i) => (i === index ? { ...d, qty: v } : d)))}
            />
          </Field>
        </div>
      ))}

      <VariantPicker onPick={(variantId, label) => setDraft([...draft, { variantId, label, qty: '1' }])} />

      {save.isError && <ErrorBanner error={save.error} />}

      <Button
        tone="primary"
        disabled={draft.length === 0 || incomplete || save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'تُحفظ…' : 'حفظُ السطور'}
      </Button>
    </div>
  )
}

function VariantPicker({ onPick }: { onPick: (variantId: string, label: string) => void }) {
  const [term, setTerm] = useState('')
  const [productId, setProductId] = useState<string | null>(null)

  const results = useQuery({
    queryKey: ['products', term],
    queryFn: () => api<ProductListItem[]>(`/api/v1/products?limit=20&q=${encodeURIComponent(term)}`),
    enabled: term.trim().length > 1 && productId === null,
  })

  const product = useQuery({
    queryKey: ['product', productId],
    queryFn: () => api<Product>(`/api/v1/products/${productId}`),
    enabled: productId !== null,
  })

  if (productId !== null) {
    return (
      <div className="rounded-md border border-dashed border-line p-3">
        {product.isPending && <Spinner />}

        {product.data && (
          <>
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium">{product.data.nameAr}</span>
              <Button onClick={() => setProductId(null)}>تراجع</Button>
            </div>

            <ul className="space-y-1">
              {product.data.variants.filter((v) => v.isActive).map((variant) => (
                <li key={variant.id}>
                  <button
                    type="button"
                    className="w-full rounded-md px-2 py-1 text-start text-sm hover:bg-surface"
                    onClick={() => {
                      const parts = [variant.variantName, variant.colour, variant.capacity].filter(Boolean)
                      onPick(variant.id, `${product.data!.nameAr} · ${parts.join(' · ') || 'الافتراضيّ'}`)
                      setProductId(null)
                      setTerm('')
                    }}
                  >
                    {[variant.variantName, variant.colour, variant.capacity].filter(Boolean).join(' · ')
                      || 'المتغيّرُ الافتراضيّ'}
                    <span className="tabular mx-2 text-xs text-ink-soft" dir="ltr">{variant.sku}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="rounded-md border border-dashed border-line p-3">
      <Field label="أضِف صنفًا">
        <TextInput value={term} onChange={setTerm} placeholder="ابحث بالاسم أو الطراز…" />
      </Field>

      {results.isPending && term.trim().length > 1 && <Spinner />}

      <ul className="mt-2 space-y-1">
        {(results.data ?? []).map((row) => (
          <li key={row.id}>
            <button
              type="button"
              className="w-full rounded-md px-2 py-1 text-start text-sm hover:bg-surface"
              onClick={() => setProductId(row.id)}
            >
              {row.nameAr}
              <span className="tabular mx-2 text-xs text-ink-soft" dir="ltr">{row.code}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
